import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { scrollToJoin } from "@/App";
import { ReceiptCard, ReceiptFloatExtras } from "./ReceiptCard";

const GHOSTS = [
  { text: "Only admins can post here", cls: "top-[18%] left-[6%] -rotate-6" },
  { text: "Comments are turned off", cls: "top-[38%] right-[4%] rotate-3" },
  { text: "100 seats left — ends tonight", cls: "bottom-[14%] left-[10%] rotate-2" },
];

const EASE = [0.22, 1, 0.36, 1];

const HEADLINE = [
  <>You paid <span className="text-warn">₹299</span>.</>,
  <>You got a folder of{" "}<em className="not-italic underline decoration-gold decoration-4 underline-offset-8">free files</em></>,
  <>someone downloaded and <span className="text-warn">resold</span>.</>,
];

export const Hero = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const ghostY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const blobY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const cardLift = useSpring(useTransform(scrollYProgress, [0, 1], [0, -90]), { stiffness: 60, damping: 20 });

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 140, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 140, damping: 18 });

  const onTilt = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const resetTilt = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section ref={sectionRef} data-testid="hero-section" className="relative overflow-hidden pt-36 pb-24 sm:pt-44 sm:pb-32">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <motion.div style={{ y: blobY }} className="absolute inset-0">
          <div className="absolute -top-40 -right-40 w-[34rem] h-[34rem] rounded-full bg-trust/10 blur-3xl" />
          <div className="absolute top-1/2 -left-52 w-[28rem] h-[28rem] rounded-full bg-gold/15 blur-3xl" />
        </motion.div>
        <motion.div style={{ y: ghostY }} className="absolute inset-0">
          {GHOSTS.map((g) => (
            <span
              key={g.text}
              className={`absolute ${g.cls} hidden lg:block rounded-full border border-black/5 bg-white/60 px-4 py-2 text-xs font-semibold text-gray-400 select-none`}
            >
              {g.text}
            </span>
          ))}
        </motion.div>
      </div>

      <div className="relative max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <motion.span
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2.5 rounded-full border border-verified/25 bg-verified-soft px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-verified"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
          >
            <span className="pulse-dot w-2 h-2 rounded-full bg-verified" />
            For everyone who paid and got nothing
          </motion.span>

          <h1 className="mt-7 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.04]">
            {HEADLINE.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.85, delay: 0.15 + i * 0.13, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            className="mt-6 text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75, ease: EASE }}
          >
            Slick ad. Real-looking video. Then you download it — and it's junk. A community where buyers finally
            compare notes <b className="text-ink">before</b> they pay, backed by real payment receipts, not opinions.
          </motion.p>

          <motion.div
            className="mt-9 flex flex-col sm:flex-row sm:items-center gap-4"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.92, ease: EASE }}
          >
            <button
              data-testid="hero-join-button"
              onClick={scrollToJoin}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-trust text-white font-semibold px-8 py-4 transition-colors duration-200 hover:bg-trust-dark"
            >
              Join the first reviewers
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <p className="text-sm text-gray-500">
              <b className="text-ink">Free.</b> Be one of the first 500 to shape it.
            </p>
          </motion.div>
        </div>

        <motion.div
          className="relative max-w-md w-full mx-auto lg:ml-auto"
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
        >
          <motion.div style={{ y: cardLift }}>
            <motion.div
              onMouseMove={onTilt}
              onMouseLeave={resetTilt}
              style={{ rotateX, rotateY, transformStyle: "preserve-3d", perspective: 1000 }}
            >
              <ReceiptCard testId="hero-receipt-card" />
            </motion.div>
          </motion.div>
          <ReceiptFloatExtras />
        </motion.div>
      </div>
    </section>
  );
};
